/**
 * TerrainMeshComponent procedurally generates a height-mapped terrain mesh.
 * Builds a plane geometry and displaces vertices using random or Perlin noise.
 * Integrates with physics when enabled to create a static collision body.
 */
import * as THREE from 'three';

import { MaterialUtils } from '../../materials.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import Perlin from '../../utils/Perlin.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';


type NoiseType = 'random' | 'perlin';

export interface TerrainMeshComponentOptions extends PrimitiveComponentOptions {
  width?: number;
  depth?: number;
  widthSegments?: number;
  depthSegments?: number;
  maxHeight?: number;
  material?: GenericMaterialType;
  noiseType?: NoiseType
  noiseFrequency?: number,
  noiseSeed?: number,
  uvScaleTilesPerUnit?: number,
}

@EngineClass('TerrainMeshComponent')
export class TerrainMeshComponent extends PrimitiveComponent {




  @property()
  public width: number = 200;

  @property()
  public depth: number = 200;

  @property()
  public widthSegments: number = 200;

  @property()
  public depthSegments: number = 200;

  @property()
  public maxHeight: number = 5;

  @property()
  public terrainMaterial: GenericMaterialType | null = null;

  @property()
  public noiseType: NoiseType = 'perlin';

  @property()
  public noiseFrequency: number = 0.1;

  @property()
  public noiseSeed: number = 100;

  @property()
  public uvScaleTilesPerUnit: number | null = null;

  private heightMap: Float32Array | null = null;
  private mesh!: THREE.Mesh;

  public override initialize(options?: TerrainMeshComponentOptions): void {
    // Set default physics options for terrain
    const defaultOptions: TerrainMeshComponentOptions = {
      physicsOptions: {
        enabled: true,
        motionType: PhysicsMotionType.Static,
        density: 1
      },
      ...options
    };

    super.initialize(defaultOptions);

    this.width = options?.width ?? this.width;
    this.depth = options?.depth ?? this.depth;
    this.widthSegments = options?.widthSegments ?? this.widthSegments;
    this.depthSegments = options?.depthSegments ?? this.depthSegments;
    this.maxHeight = options?.maxHeight ?? this.maxHeight;
    this.terrainMaterial = options?.material ?? this.terrainMaterial;
    this.noiseType = options?.noiseType ?? this.noiseType;
    this.noiseFrequency = options?.noiseFrequency ?? this.noiseFrequency;
    this.noiseSeed = options?.noiseSeed ?? this.noiseSeed;
    this.uvScaleTilesPerUnit = options?.uvScaleTilesPerUnit ?? this.uvScaleTilesPerUnit;

    // Set default material if not provided
    this.terrainMaterial ??= new THREE.MeshStandardMaterial();

    // Create a placeholder geometry initially
    const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
    this.mesh = new THREE.Mesh(geometry);
    this.bindObject3DProperties(this.mesh, ['castShadow', 'receiveShadow']);
    this.add(this.mesh);
    if (this.terrainMaterial) {
      this.setMaterial(this.terrainMaterial);
    }

    this.generateTerrain();
  }

  public getMesh(): THREE.Mesh {
    return this.mesh;
  }

  public setMaterial(material: GenericMaterialType): void {
    // Use getLatest to prevent race conditions when material changes rapidly
    getLatest(
      this.mesh,
      'material',
      () => resourceManager.loadGenericMaterial(material)
    ).then((loadedMaterial) => {
      if (loadedMaterial) {
        this.mesh.material = loadedMaterial;
      }
    });
  }

  private applyHeightMap(vertices: ArrayLike<number>): void {
    if (!this.heightMap) return;

    // Fix: Convert ArrayLike to a mutable array
    const verticesArray = new Float32Array(vertices);

    // Apply height map to the y-coordinate of each vertex
    for (let i = 0; i < verticesArray.length; i += 3) {
      const index = Math.floor(i / 3);
      // Y is the up axis after rotation
      verticesArray[i + 1] = this.heightMap[index];
    }

    // Update the original buffer with our modified values
    for (let i = 0; i < vertices.length; i++) {
      (vertices as any)[i] = verticesArray[i];
    }
  }

  public setHeightMap(heightMap: Float32Array): void {
    if (heightMap.length !== (this.widthSegments + 1) * (this.depthSegments + 1)) {
      console.error('Height map size does not match terrain dimensions');
      return;
    }

    this.heightMap = heightMap;
    this.generateFromHeightMap();
    if (this.uvScaleTilesPerUnit) {
      MaterialUtils.scaleUVsBySize(this.mesh, this.uvScaleTilesPerUnit);
    }
  }

  public generateTerrain(frequency?: number, maxHeight?: number, seed?: number, noiseType?: NoiseType): void {
    frequency = frequency ?? this.noiseFrequency;
    maxHeight = maxHeight ?? this.maxHeight;
    seed = seed ?? this.noiseSeed;
    noiseType = noiseType ?? this.noiseType;

    const generator = new Perlin(seed);

    // always recreate the height map to support regeneration
    this.heightMap = new Float32Array((this.widthSegments + 1) * (this.depthSegments + 1));

    // Generate heights using noise
    if (noiseType === 'random') {
      let index = 0;
      for (let z = 0; z <= this.depthSegments; z++) {
        for (let x = 0; x <= this.widthSegments; x++) {
          const height = generator.simplex2(x * frequency, z * frequency);
          this.heightMap[index++] = height * maxHeight;
        }
      }
    } else {
      let index = 0;
      for (let z = 0; z <= this.depthSegments; z++) {
        for (let x = 0; x <= this.widthSegments; x++) {
          const height = generator.perlin2(x * frequency, z * frequency);
          this.heightMap[index++] = height * maxHeight;
        }
      }
    }

    this.generateFromHeightMap();
    if (this.uvScaleTilesPerUnit) {
      MaterialUtils.scaleUVsBySize(this.mesh, this.uvScaleTilesPerUnit);
    }
  }

  private generateFromHeightMap() {
    // Create a plane geometry for the terrain
    const geometry = new THREE.PlaneGeometry(
      this.width,
      this.depth,
      this.widthSegments,
      this.depthSegments
    );

    // Rotate the plane to be horizontal (facing up)
    geometry.rotateX(-Math.PI / 2);

    // Get the position attribute from the geometry
    const positionAttribute = geometry.getAttribute('position');
    const vertices = positionAttribute.array;

    // Apply the height map to the vertices
    this.applyHeightMap(vertices);

    // Update the position attribute
    positionAttribute.needsUpdate = true;

    // Update normals
    geometry.computeVertexNormals();

    // Set the new geometry
    this.mesh.geometry.dispose();
    this.mesh.geometry = geometry;
  }

  public setMaxHeight(height: number, regenerate: boolean = true): void {
    this.maxHeight = height;
    if (regenerate && this.heightMap) {
      this.generateTerrain();
    }
  }

  public getMaxHeight(): number {
    return this.maxHeight;
  }

  public updateTerrainDimensions(
    width?: number,
    depth?: number,
    widthSegments?: number,
    depthSegments?: number
  ): void {
    if (width !== undefined) this.width = width;
    if (depth !== undefined) this.depth = depth;
    if (widthSegments !== undefined) this.widthSegments = widthSegments;
    if (depthSegments !== undefined) this.depthSegments = depthSegments;

    this.generateTerrain();
  }

  public getHeightAt(x: number, z: number): number | null {
    if (!this.heightMap) return null;

    // Convert world coordinates to terrain coordinates
    const localX = x + this.width / 2;
    const localZ = z + this.depth / 2;

    // Convert to UV coordinates (0-1)
    const u = localX / this.width;
    const v = localZ / this.depth;

    // Check if the point is within the terrain bounds
    if (u < 0 || u > 1 || v < 0 || v > 1) {
      return null;
    }

    // Convert to grid coordinates
    const gridX = Math.min(Math.floor(u * this.widthSegments), this.widthSegments - 1);
    const gridZ = Math.min(Math.floor(v * this.depthSegments), this.depthSegments - 1);

    // Get the index in the height map
    const index = gridZ * (this.widthSegments + 1) + gridX;

    return this.heightMap[index];
  }

  public getHeightMap(): Float32Array | null {
    return this.heightMap;
  }

  public override syncFromPhysics(position: THREE.Vector3, quat: THREE.Quaternion): this {
    return this;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_TerrainMesh';
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (['width', 'depth', 'widthSegments', 'depthSegments', 'maxHeight', 'noiseType', 'noiseFrequency', 'noiseSeed', 'uvScaleTilesPerUnit'].includes(path)) {
      this.generateTerrain();
      return;
    }
    if (['material'].includes(path)) {
      this.setMaterial(value);
      return;
    }
  }
}
